// useSidebarMenu.ts — يجلب/يبني قائمة التنقّل (منقول من منطق تحميل البيانات في Sidebar.js):
// app-menu + role-menu + me/permissions + me/menu → قائمة نهائيّة مُصفّاة ومرتَّبة.
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { readJsonStorage, readStorage, writeJsonStorage, MENU_REFRESH_EVENT, PERMISSIONS_UPDATED_EVENT } from '../lib/storage';
import type { Permission } from '../lib/accessControl';
import {
	buildMenuFromPermissions,
	hideOwnerPortalPaths,
	orderAccessControlAfterSettings,
	prepareMenuItems,
	sortMenuItems,
	type MenuItem,
} from './menuLogic';

interface RoleMenuRow {
	role_id: number | string;
	menu_id: number | string;
	allowed: number | string;
}

function useRoleIds(): number[] {
	const [roleIds, setRoleIds] = useState<number[]>(() => {
		const lsRole = Number(readStorage('role_id') || 0);
		return lsRole ? [lsRole] : [1];
	});

	useEffect(() => {
		const update = () => {
			const lsRole = Number(readStorage('role_id') || 0);
			setRoleIds(lsRole ? [lsRole] : [1]);
		};
		window.addEventListener(PERMISSIONS_UPDATED_EVENT, update);
		return () => window.removeEventListener(PERMISSIONS_UPDATED_EVENT, update);
	}, []);

	return roleIds;
}

export function useSidebarMenu(): { menuItems: MenuItem[]; loading: boolean } {
	const roleIds = useRoleIds();
	const roleKey = roleIds.join(',');
	const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [refreshTick, setRefreshTick] = useState(0);

	useEffect(() => {
		const refresh = () => setRefreshTick((v) => v + 1);
		window.addEventListener(MENU_REFRESH_EVENT, refresh);
		return () => window.removeEventListener(MENU_REFRESH_EVENT, refresh);
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			try {
				const [allMenuRes, roleMenuRes, permissionsRes, myMenuRes] = await Promise.allSettled([
					api.get<MenuItem[]>('/app-menu'),
					api.get<RoleMenuRow[]>('/role-menu'),
					api.get<{ permissions?: Record<string, boolean> }>('/me/permissions'),
					api.get<{ items?: MenuItem[] }>('/me/menu'),
				]);
				if (cancelled) return;

				const allMenu = allMenuRes.status === 'fulfilled' ? allMenuRes.value.data : [];
				const roleMenu = roleMenuRes.status === 'fulfilled' ? roleMenuRes.value.data : [];
				const permissionsPayload = permissionsRes.status === 'fulfilled' ? permissionsRes.value.data.permissions : undefined;
				const myMenuItems = myMenuRes.status === 'fulfilled' ? myMenuRes.value.data.items : undefined;

				if (permissionsPayload) {
					const mapped: Permission[] = Object.entries(permissionsPayload).map(([page_name, allowed]) => ({
						page_name,
						can_access: allowed ? 1 : 0,
					}));
					writeJsonStorage('permissions', mapped);
				}

				const activeMenu = (Array.isArray(allMenu) ? allMenu : []).filter((m) => Number(m.is_active) === 1);
				writeJsonStorage('app_menu_all', Array.isArray(allMenu) ? allMenu : []);

				const allowedMenuIds = new Set<number>();
				if (Array.isArray(roleMenu)) {
					for (const row of roleMenu) {
						if (roleIds.includes(Number(row.role_id)) && Number(row.allowed) === 1) {
							allowedMenuIds.add(Number(row.menu_id));
						}
					}
				}
				const hasRoleMenuRows = Array.isArray(roleMenu) && roleMenu.some((row) => roleIds.includes(Number(row.role_id)));

				const userMenu = Array.isArray(myMenuItems) ? myMenuItems.filter((m) => Number(m.is_active) === 1) : [];

				const filtered: MenuItem[] = userMenu.length
					? userMenu
					: hasRoleMenuRows
						? activeMenu.filter((m) => allowedMenuIds.has(Number(m.menu_id)))
						: buildMenuFromPermissions(
								permissionsPayload
									? Object.entries(permissionsPayload).map(([page_name, allowed]) => ({
											page_name,
											can_access: allowed ? 1 : 0,
										}))
									: readJsonStorage<Permission[]>('permissions', []),
							);

				const sorted = orderAccessControlAfterSettings(
					hideOwnerPortalPaths(prepareMenuItems(sortMenuItems(filtered))),
				);
				setMenuItems(sorted);
				writeJsonStorage('me_menu', sorted);
			} catch {
				// فشل كامل النداءات → ابنِ القائمة من permissions المحليّة (احتياطيّ).
				const perms = readJsonStorage<Permission[]>('permissions', []);
				const built = orderAccessControlAfterSettings(hideOwnerPortalPaths(prepareMenuItems(buildMenuFromPermissions(perms))));
				setMenuItems(built);
				writeJsonStorage('me_menu', built);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// roleKey (سلسلة نصّيّة مشتقّة من roleIds.join(',')) تُستخدَم عمدًا بدل roleIds نفسها:
		// roleIds مصفوفة تتغيّر مرجعيّتها كلّ Render، فإضافتها هنا تُعيد حلقة تشغيل لا نهائيّة.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [roleKey, refreshTick]);

	return { menuItems, loading };
}
